import { gql } from "@apollo/client";
import React, { useRef } from "react";
import { textVariants } from "@src/ui/text";
import { cn } from "@src/ui/lib/utils";
import { useDispatchSetTrainingResourceNameAction } from "../hooks/useDispatchSetTrainingResourceNameAction";
import { useAtomValue } from "jotai/react";
import { trainingResourceTitleAtom } from "../atoms";
import useTranslationModeContext from "../../course/hooks/useTranslationModeContext";
import {
  useTranslation,
  updateTranslationSet,
} from "@src/utils/translationSets";

const TrainingResourceTitleInput: React.FC = () => {
  const dispatchSetTrainingResourceNameAction =
    useDispatchSetTrainingResourceNameAction();
  const trainingResourceTitle = useAtomValue(trainingResourceTitleAtom);
  const translationModeContext = useTranslationModeContext();

  const titleTranslation = useTranslation(
    trainingResourceTitle,
    translationModeContext?.currentLang || "en",
  );

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  // Adjust height whenever the title changes
  React.useEffect(() => {
    adjustTextareaHeight();
  }, [titleTranslation.text]);

  // Focus only on mount
  React.useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.focus();
      const length = textarea.value.length;
      textarea.setSelectionRange(length, length);
    }
  }, []);

  return (
    <textarea
      ref={textareaRef}
      className={cn(
        textVariants({ variant: "H1" }),
        "w-full font-bold resize-none overflow-hidden",
      )}
      placeholder="Document Title..."
      value={titleTranslation.text || ""}
      onChange={(e) => {
        if (!trainingResourceTitle || !translationModeContext) return;

        const updatedTranslationSet = updateTranslationSet({
          ts: trainingResourceTitle,
          lang: translationModeContext.currentLang,
          text: e.target.value,
        });

        dispatchSetTrainingResourceNameAction({
          newName: updatedTranslationSet,
          oldName: trainingResourceTitle,
        });
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
        }
      }}
      rows={1}
    />
  );
};

gql`
  mutation TrainingResourceTitleInput_UpdateTrainingResource(
    $trainingResourceId: String!
    $input: UpdateTrainingResourceInput!
  ) {
    updateTrainingResource(
      trainingResourceId: $trainingResourceId
      input: $input
    ) {
      success
      trainingResource {
        id
      }
    }
  }
`;

export default TrainingResourceTitleInput;
